import { ForwardedRef, forwardRef, useState } from 'react'
import { HeaderGroup } from 'react-table'

import { Icon, Text, Tooltip } from 'components'

import { FixMeAny } from 'types/global'

import { StyledHeader, HeaderWrapper, SortContainer, TitleContainer } from './styles'
import { TableVariant, TableVariantType } from '.'

type Props = {
  withCheckbox?: boolean
  tHeadClassName: string
  headerGroups: Array<HeaderGroup<FixMeAny>>
  onSort?: (field: string, order: SorterStatus) => void
  initialSortBy?: {
    id: string
    order: SorterStatus
  }
  scrollLeft?: number
  borderless: boolean
  variant?: TableVariant
}

export type SorterStatus = 'default' | 'asc' | 'desc'

const getSorterState = (sorterState: SorterStatus) => {
  switch (sorterState) {
    case 'default':
      return 'asc'
    case 'asc':
      return 'desc'
    case 'desc':
      return 'default'
    default:
      return 'default'
  }
}

const TableHeader = forwardRef<HTMLTableSectionElement, Props>(
  (props: Props, ref: ForwardedRef<HTMLTableSectionElement>) => {
    const {
      initialSortBy = { id: '', order: 'default' },
      withCheckbox,
      tHeadClassName,
      headerGroups,
      onSort,
      scrollLeft = 0,
      borderless,
      variant = TableVariantType.default,
    } = props
    const [currSorterState, setCurrSorterState] = useState<SorterStatus>(initialSortBy.order)
    const [currColumn, setCurrColumn] = useState(initialSortBy.id)

    const handleSorterChange = (columnId: string, sortOrder: SorterStatus) => {
      if (columnId === currColumn) {
        const updatedSorterState = getSorterState(sortOrder)
        setCurrSorterState(updatedSorterState)
        onSort?.(columnId, updatedSorterState)
      } else {
        const updatedSorterState = getSorterState('default')
        setCurrSorterState(updatedSorterState)
        setCurrColumn(columnId)
        onSort?.(columnId, updatedSorterState)
      }
    }

    return (
      <div className={tHeadClassName} ref={ref} style={{ overflow: 'hidden' }}>
        {headerGroups.map((headerGroup, index) => (
          <div
            {...headerGroup.getHeaderGroupProps()}
            key={index}
            style={{ ...headerGroup.getHeaderGroupProps().style, left: scrollLeft * -1, position: 'relative' }}
          >
            {headerGroup.headers.map((column, id) => {
              const isCheckboxColumn = withCheckbox && id === 0
              const headerClassName: string = (column as FixMeAny).headerClassName ?? ''
              const { style, ...headerProps } = onSort
                ? column.getHeaderProps()
                : column.getHeaderProps(column.getSortByToggleProps())

              return (
                <StyledHeader
                  variant={variant}
                  borderless={borderless}
                  className={headerClassName}
                  {...headerProps}
                  key={id}
                  style={{
                    ...style,
                    flex: isCheckboxColumn ? '' : style?.flex,
                    display: isCheckboxColumn ? 'grid' : style?.display,
                    alignItems: isCheckboxColumn ? 'center' : style?.alignItems,
                    justifyContent: isCheckboxColumn ? 'center' : style?.justifyContent,
                    minWidth: isCheckboxColumn ? '50px' : '0',
                  }}
                >
                  <HeaderWrapper
                    {...column}
                    onClick={column.canSort ? () => handleSorterChange(column.id, currSorterState) : undefined}
                  >
                    {isCheckboxColumn ? (
                      <Text whiteSpace="nowrap" fontWeight="medium" isCapitalizedFirstLetter>
                        {column.render('Header')}
                      </Text>
                    ) : (
                      <TitleContainer>
                        <Tooltip title={column.render('Header')} position="bottom">
                          <Text ellipsis fontWeight="medium" isCapitalizedFirstLetter color="gray5">
                            {column.render('Header')}
                          </Text>
                        </Tooltip>
                      </TitleContainer>
                    )}
                    {isCheckboxColumn ? null : column.canSort ? (
                      <SortContainer>
                        {currSorterState === 'desc' && currColumn === column.id ? (
                          <Icon icon="caret-down" />
                        ) : currSorterState === 'asc' && currColumn === column.id ? (
                          <Icon icon="caret-up" />
                        ) : (
                          <Icon icon="sort" />
                        )}
                      </SortContainer>
                    ) : null}
                  </HeaderWrapper>
                  {!headerClassName.includes('fixed') && (
                    <div
                      {...column.getResizerProps()}
                      className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    />
                  )}
                </StyledHeader>
              )
            })}
          </div>
        ))}
      </div>
    )
  },
)

export default TableHeader
